import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";

/** A real modal: inert background, focus containment, Escape and focus return. */
export function SurfaceDialog({ className, labelledBy, children, onClose, onKeyDown, busy = false }: {
  className: string; labelledBy: string; children: ReactNode;
  onClose: () => void; onKeyDown?: (event: KeyboardEvent<HTMLDialogElement>) => void; busy?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    const previous = document.activeElement as HTMLElement | null;
    if (!dialog) return;
    if (typeof dialog.showModal === "function") dialog.showModal(); else dialog.setAttribute("open", "");
    (dialog.querySelector<HTMLElement>("[data-initial-focus]") ?? dialog.querySelector<HTMLElement>("button, textarea"))?.focus();
    return () => { if (dialog.open && typeof dialog.close === "function") dialog.close(); if (previous?.isConnected) previous.focus({ preventScroll: true }); };
  }, []);
  return createPortal(<dialog ref={ref} className={`surface-dialog ${className}`} aria-labelledby={labelledBy} aria-modal="true" aria-busy={busy}
    onCancel={event => { event.preventDefault(); if (!busy) onClose(); }}
    onKeyDown={event => { event.stopPropagation(); onKeyDown?.(event); if (event.key === "Escape" && !event.defaultPrevented) { event.preventDefault(); if (!busy) onClose(); } }}
    onClick={event => {
      if (event.target !== event.currentTarget || busy) return;
      const rect = event.currentTarget.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onClose();
    }}>{children}</dialog>, document.body);
}
